import { Box, Button, Title, BackButton, useToggle, SupportedCurrencies } from 'vopay';

import { FunctionComponent, useContext } from 'react';
import { Prompt, useParams, useLocation } from 'react-router-dom';

import AccountCredentialsPanel from './SubAccountCredentialsPanel';
import AccountOnboardingPanel from './SubAccountOnboardingPanel';
import AccountTabs from './SubAccountTabs';
import AccountContext from '../AccountContext';

import { usePendingData } from '@/providers/PendingData.provider';
import { useModuleFeatureFlag } from '@/providers/FeatureFlag.provider';

import type { AccountsHandlers, Balances as Balance } from '../types';

type SubAccountProps = {
  hasValidOnboardingUrl: boolean | null;
  handlers: AccountsHandlers;
  onCopyClick: () => void;
  onActionClick: () => void;
  onBoardingLink: string | null;
};

type FC = FunctionComponent<SubAccountProps>;

const SubAccount: FC = ({
  hasValidOnboardingUrl,
  onCopyClick,
  onActionClick,
  onBoardingLink,
  handlers,
}: SubAccountProps) => {
  const { details, transactions } = useContext(AccountContext);
  const { hasPendingData } = usePendingData();

  const { canAccessOnboardingTokenPanel, canSeeAccountCredential } = useModuleFeatureFlag('accounts');

  const { accountId } = useParams<{ accountId: string }>();

  if (!details[accountId]) return <></>;
  const query = new URLSearchParams(useLocation().search);
  const balance: Balance = {
    account: query.get('account') || '0',
    pending: query.get('pending') || '0',
    available: query.get('available') || '0',
    immediate: query.get('immediate') || '0',
    reserve: query.get('reserve') || '0',
    currency: (query.get('currency') as SupportedCurrencies) || 'CAD',
    isActive: query.get('isActive') === 'true',
    accountStatus: query.get('accountStatus') || '',
    security: query.get('security') || '',
  };

  const [showCredentials, setShowCredentials] = useToggle(false);
  return (
    <>
      {canAccessOnboardingTokenPanel && balance.accountStatus === 'Application Pending' && (
        <Box width="50em" jc="fe">
          <AccountOnboardingPanel
            hasValidOnboardingUrl={hasValidOnboardingUrl}
            onActionClick={onActionClick}
            onCopyClick={onCopyClick}
            onBoardingLink={onBoardingLink}
          />
        </Box>
      )}
      {canSeeAccountCredential && showCredentials && <AccountCredentialsPanel accountId={accountId} />}

      <Box flx="col">
        <Box gap="common" ai="fs">
          <Box>
            <BackButton variant="arrow" backButtonEnabled size="sm" />
          </Box>
          <Box flx="col">
            <Title title={details[accountId].accountName} />
            {details[accountId].ParentAccountName && (
              <Box pt="md" pb="lg">
                Belongs to {details[accountId].ParentAccountName}
              </Box>
            )}
          </Box>
        </Box>

        <Box pt="md" pb="md" gap="sm">
          {canSeeAccountCredential && (
            <Button
              id="showHideAccountCredentials"
              label={`${showCredentials ? 'Hide' : 'Show'} Credentials`}
              color="primary"
              size="sm"
              onClick={() => setShowCredentials()}
            />
          )}
        </Box>
      </Box>

      <AccountTabs transactions={transactions} handlers={handlers} />

      <Prompt when={hasPendingData} message="You have unsaved Account Permissions that will be discarded." />
    </>
  );
};

export default SubAccount;
